import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { createMask } from '@ngneat/input-mask';
import { isNil, remove } from 'lodash';
import { Subscription } from 'rxjs';
import { CompanyVendorForm } from 'src/app/classes/form-groups/company-vendor-form';
import { SmallBusinessOptionForm } from 'src/app/classes/form-groups/small-business-option-form';
import { VendorOperatingStatesForm } from 'src/app/classes/form-groups/vendor-operating-states-form';
import {
  Address,
  CompanyOffice,
  SubNationalDivision,
  Vendor
} from 'src/app/classes/models';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { RegistrationProgress } from 'src/app/interfaces/models/registration-progress';
import { SmallBusinessOptionType } from 'src/app/interfaces/small-business-option-type';
import { StockExchange } from 'src/app/interfaces/stock-exchanges';
import { USCompanyType } from 'src/app/interfaces/us-company-type';
import { AuthService } from 'src/app/services/auth.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';
import {
  DBA_VALIDATION_ERROR_MESSAGE,
  isInvalidDba
} from 'src/app/validators/dba-validator';
import { einSsnValidator } from 'src/app/validators/ein-ssn-validator';

@Component({
  selector: 'app-vendor-edit',
  templateUrl: './vendor-edit.component.html',
  styleUrls: ['./vendor-edit.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorEditComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);
  constructor(
    private SubNationalDivisionService: SubNationalDivisionService,
    private ToastService: ToastService,
    private VendorService: VendorService,
    private AuthService: AuthService,
    private UtilityService: UtilityService
  ) {
    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (role) {
      if (
        this.fullSSNPermissions.has(role) ||
        this.maskedSSNPermissions.has(role)
      ) {
        this.hasSSNAccess = true;
      }
    }
  }

  @Input() vendorForm!: FormGroup;
  @Input() streetAddressForm!: FormGroup;
  @Input() mailingAddressForm!: FormGroup;
  @Input() remitToAddressForm!: FormGroup;
  @Input() streetAddress?: Address;
  @Input() mailingAddress?: Address;
  @Input() remitToAddress?: Address;
  @Input() localizationInfo!: LocalizationInfo;
  @Input() VendorId!: number;
  @Input()
  selectedSmallBusinessOptionsFormArray!: FormArray<SmallBusinessOptionForm>;
  @Input() ClientCompanyVendorMap!: Map<number, CompanyVendorForm>;
  @Input() CompanyIdToCompanyOfficesMap!: Map<number, CompanyOffice[]>;
  @Input() OperatingStatesForm?: VendorOperatingStatesForm;

  Vendor!: Vendor;
  tagfieldDropdownOpen = false;

  states!: SubNationalDivision[];

  stockExchanges: StockExchange[] = [
    'NYSE',
    'NASDAQ',
    'Japan Exchange Group',
    'Shanghai Stock Exchange',
    'Euronext',
    'London Stock Exchange',
    'Hong Kong Stock Exchange',
    'Shenzhen Stock Exchange',
    'TMX Group',
    'National Stock Exchange of India',
    'Deutsche Börse',
    'Bombay Stock Exchange',
    'Korea Exchange',
    'SIX Swiss Exchange',
    'Nasdaq Nordic',
    'Australian Securities Exchange',
    'JSE Limited',
    'Taiwan Stock Exchange',
    'BM&F Bovespa',
    'BME Spanish Exchanges',
    'Other'
  ];

  employeeQuantityOptions: {
    label: string;
    value: number;
  }[] = [
    { label: '0 Employees', value: 0 },
    { label: '1 Employee', value: 1 },
    { label: '2 Employees', value: 2 },
    { label: '3 Employees', value: 3 },
    { label: '4 Employees', value: 4 },
    { label: '5 - 10 Employees', value: 5 },
    { label: '11 - 25 Employees', value: 10 },
    { label: '26 - 50 Employees', value: 25 },
    { label: '51 - 100 Employees', value: 50 },
    { label: '101 - 500 Employees', value: 100 },
    { label: '501+ Employees', value: 500 }
  ];

  usTaxStructures: USCompanyType[] = [
    'C Corporation',
    'S Corporation',
    'Limited Liability Company (LLC)',
    'Partnership',
    'Sole Proprietorship',
    'Trust'
  ];

  caTaxStructures: string[] = [
    'Sole Proprietorship',
    'Partnership',
    'Corporation'
  ];

  llcTypes: {
    label: string;
    value: string;
  }[] = [
    { label: 'Partnership', value: 'P' },
    { label: 'C-Corp', value: 'C' },
    { label: 'S-Corp', value: 'S' }
  ];

  smallBusinessOptions = new Map<SmallBusinessOptionType, boolean>([
    ['Minority Owned', false],
    ['Woman Owned', false],
    ['Veteran Owned', false],
    ['Disability Owned', false],
    ['8(a) SBA Certified', false]
  ]);

  sharedDataServiceListenEventSubscription?: Subscription;

  yesNoOptions: {
    label: 'Yes' | 'No';
    value: boolean;
  }[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  phoneMask = createMask('(999) 999-9999');
  // US Tax Id Masks
  einMask = createMask('99-9999999');
  ssnMask = createMask('999-99-9999');

  // Canadian Tax Id Mask
  gstHstMask = createMask('999999999');

  usZipMask = createMask('99999');
  canadianZipMask = createMask('A9A 9A9');
  phoneExtensionMask = createMask({ regex: '\\d{1,6}', placeholder: '' });

  yearArray: number[] = new Array(300)
    .fill(new Date().getFullYear())
    .map((year, index) => year - index);
  maskedSSN?: string;
  fullSSN?: string;
  hasSSNAccess: boolean = false;

  editPermissions = new Set([
    'ADMIN_ADMIN',
    'ADMIN_PROJECT_MANAGEMENT_TEAM',
    'ADMIN_FRAUD_TEAM'
  ]);

  fullSSNPermissions = new Set([
    'VENDOR_ADMIN',
    ...Array.from(this.editPermissions)
  ]);

  maskedSSNPermissions = new Set([
    'ADMIN_CUSTOMER_SERVICE_TEAM',
    'ADMIN_INSURANCE_TEAM'
  ]);

  LoggedInUser = this.AuthService.getUser();

  viewSSN() {
    if (!this.LoggedInUser) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user information.',
        { returnError: true }
      );
    }

    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (!role) {
      throw this.ToastService.addErrorToast('Error', 'Missing user role.', {
        returnError: true
      });
    }

    if (this.fullSSNPermissions.has(role)) {
      return this.VendorService.getFullVendorSSN(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: this.VendorId,
          attributes: ['id', 'ssn']
        })
      ).subscribe({
        next: (Vendor) => {
          if (!Vendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Vendor.',
              { returnError: true }
            );
          }

          const { ssn } = Vendor;

          if (!ssn) {
            throw this.ToastService.addErrorToast('Error', 'Missing ssn.', {
              returnError: true
            });
          }

          Object.assign(this, { fullSSN: ssn });
        }
      });
    }

    if (this.maskedSSNPermissions.has(role)) {
      return this.VendorService.getMaskedVendorSSN(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: this.VendorId,
          attributes: ['id'],
          aliasedAttributes: ['maskedSSN']
        })
      ).subscribe({
        next: (Vendor) => {
          if (!Vendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Vendor.',
              { returnError: true }
            );
          }

          const { maskedSSN } = Vendor;
          Object.assign(this, { maskedSSN });
        }
      });
    }

    return;
  }

  showMaskedSSN() {
    if (!this.LoggedInUser) {
      return true;
    }

    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    // These teams must show the button. Other permissions can view SSN in entirety.
    // This could change.
    if (
      role &&
      new Set([
        'ADMIN_PRIME_TEAM',
        'ADMIN_EMAILS_TEAM',
        'ADMIN_INSURANCE_TEAM',
        'ADMIN_CUSTOMER_SERVICE_TEAM'
      ]).has(role)
    ) {
      return true;
    }

    return false;
  }

  hasEditPermissions() {
    if (this.LoggedInUser) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;

      return role && new Set(['ADMIN_ADMIN', 'ADMIN_FRAUD_TEAM']).has(role);
    }

    return false;
  }

  handleTaxIdOrLegalNameClick(event: Event) {
    const { hasSignedAnyVendorAgreement, hasMultipleClients } = this.Vendor;
    if (
      hasSignedAnyVendorAgreement &&
      hasMultipleClients &&
      !this.hasEditPermissions()
    ) {
      event.preventDefault();
      throw this.ToastService.addErrorToast(
        'Error',
        'Unable to update Tax ID or Legal Name. Please contact customer service for further assistance.',
        { returnError: true }
      );
    }
  }

  preserveMapOrder = this.UtilityService.keyValuePairDefaultOrder;

  onEinSsnChange() {
    const {
      controls: { ein: einControl, ssn: ssnControl }
    } = this.vendorForm;
    const { value: ein } = einControl;
    const { value: ssn } = ssnControl;
    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (ein && !ssn) {
      ssnControl.disable();
      ssnControl.patchValue(null, { emitViewToModelChange: false });
    } else {
      ssnControl.enable();
    }

    if (
      (ssn || (this.Vendor.hasSSN && !this.fullSSNPermissions.has(role!))) &&
      !ein
    ) {
      einControl.disable();
      einControl.patchValue(null, { emitViewToModelChange: false });
    } else {
      einControl.enable();
    }
  }

  generateSmallBusinessOptionFormGroup({
    id,
    VendorId,
    type
  }: {
    id?: number;
    VendorId: number;
    type: SmallBusinessOptionType;
  }) {
    if (isNil(VendorId)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot generate small business option form group. Missing VendorId',
        { returnError: true }
      );
    }
    if (isNil(type)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot generate small business option form group. Missing type.',
        { returnError: true }
      );
    }

    return new FormGroup({
      id: new FormControl(id, { nonNullable: true }),
      VendorId: new FormControl(VendorId, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      type: new FormControl(type, {
        nonNullable: true,
        validators: [Validators.required]
      })
    });
  }

  companyTypeChanged(companyType: any) {
    if (!['Limited Liability Company (LLC)'].includes(companyType)) {
      this.vendorForm.controls['llcType'].patchValue(null);
      this.vendorForm.controls['llcType'].removeValidators(Validators.required);
    } else {
      this.vendorForm.controls['llcType'].setValidators(Validators.required);

      setTimeout(() => {
        if (!this.vendorForm.controls['llcType'].value) {
          this.vendorForm.controls['llcType'].patchValue(null);
          this.vendorForm.controls['llcType'].updateValueAndValidity();
        }
      }, 0);
    }

    this.vendorForm.controls['llcType'].updateValueAndValidity();

    if (!this.displaySSN()) {
      this.vendorForm.controls['ssn'].patchValue(null);
    } else {
      this.vendorForm.controls['ssn'].patchValue(this.Vendor.ssn ?? null);
    }
  }

  displayLLCType() {
    return ['Limited Liability Company (LLC)'].includes(
      this.vendorForm.controls['companyType'].getRawValue()
    );
  }

  displaySSN() {
    this.onEinSsnChange();

    return (
      this.vendorForm.controls['companyType'].getRawValue() ===
        'Sole Proprietorship' ||
      this.vendorForm.controls['llcType'].getRawValue() === 'disregardedEntity'
    );
  }

  requireSSN() {
    return !this.vendorForm.controls['ein'].getRawValue();
  }

  requireEIN() {
    return !this.vendorForm.controls['ssn'].getRawValue();
  }

  // Checks to see if any Client has offices requiring a Vendor selection.
  isAnyCompanyOffices() {
    return !!Array.from(this.CompanyIdToCompanyOfficesMap.values()).find(
      (CompanyOffices) => CompanyOffices.length > 0
    );
  }

  selectSmallBusinessOption([type, value]: [SmallBusinessOptionType, boolean]) {
    const SmallBusinessOptionFormGroup = new SmallBusinessOptionForm();
    SmallBusinessOptionFormGroup.patchValue({ type, VendorId: this.VendorId });
    this.selectedSmallBusinessOptionsFormArray.push(
      SmallBusinessOptionFormGroup
    );
    this.smallBusinessOptions.set(type, true);
    this.selectedSmallBusinessOptionsFormArray.markAsDirty();
  }

  deselectSmallBusinessOption(type: SmallBusinessOptionType) {
    remove(
      this.selectedSmallBusinessOptionsFormArray.controls,
      (fc) => fc.value.type === type
    );
    this.smallBusinessOptions.set(type, false);
    this.selectedSmallBusinessOptionsFormArray.markAsDirty();
  }

  toggleTagfieldDropdown() {
    this.tagfieldDropdownOpen = !this.tagfieldDropdownOpen;
  }

  @HostListener('document:click', ['$event'])
  closeTagfieldDropdownOnClickOutside(event: Event) {
    if (!this.tagfieldDropdownOpen) {
      return;
    }

    const target = event.target as HTMLElement;

    const tagfieldElement = document.getElementById('tagfield-dropdown');
    const dropdownMenu = document.querySelector(
      '[aria-labelledby="tagfield-dropdown"]'
    );

    const isClickInsideTagfield = tagfieldElement?.contains(target);
    const isClickInsideMenu = dropdownMenu?.contains(target);

    if (!isClickInsideTagfield && !isClickInsideMenu) {
      this.tagfieldDropdownOpen = false;
    }
  }

  afterHoursPhoneRequirement() {
    const allowAfterHoursService =
      this.vendorForm.controls['allowAfterHoursService'].value;
    const afterHoursPhone = this.vendorForm.controls['afterHoursPhone'];

    if (allowAfterHoursService) {
      afterHoursPhone.setValidators(Validators.required);
    } else {
      afterHoursPhone.markAsPristine();
      afterHoursPhone.clearValidators();
      afterHoursPhone.setErrors(null);
    }

    afterHoursPhone.updateValueAndValidity();
  }

  preventInput(event: KeyboardEvent) {
    const { key } = event;

    if (key !== 'Tab') {
      return false;
    }

    return event;
  }

  isPubliclyTradedCHanged() {
    const isPubliclyTraded = this.vendorForm.get('isPubliclyTraded');
    const stockExchange = this.vendorForm.get('stockExchange');
    const stockSymbol = this.vendorForm.get('stockSymbol');

    if (isPubliclyTraded?.getRawValue() === true) {
      stockExchange?.setValidators(Validators.required);
      stockSymbol?.setValidators(Validators.required);
    } else {
      stockExchange?.clearValidators();
      stockSymbol?.clearValidators();
    }

    stockExchange?.updateValueAndValidity();
    stockSymbol?.updateValueAndValidity();
  }

  updateVendorFormControlsForCountry(country: 'US' | 'CA') {
    if (country === 'US') {
      this.vendorForm.addControl('ein', new FormControl(null));
      this.vendorForm.addControl('ssn', new FormControl(null));
      this.vendorForm.addControl(
        'isPubliclyTraded',
        new FormControl(null, [Validators.required])
      );
      this.vendorForm.addValidators(einSsnValidator);
    } else if (country === 'CA') {
      this.vendorForm.addControl('usesSIN', new FormControl(null));
      this.vendorForm.addControl(
        'bsn',
        new FormControl(null, [Validators.required])
      );
    } else {
      throw this.ToastService.addErrorToast(
        'Error',
        'Country is not supported',
        { returnError: true }
      );
    }

    this.vendorForm.updateValueAndValidity();
  }

  sinChanged() {
    if (this.vendorForm.controls['usesSIN'].value) {
      this.vendorForm.controls['bsn'].disable();
      this.vendorForm.controls['bsn'].patchValue(null);
    } else {
      this.vendorForm.controls['bsn'].enable();
    }
  }

  isNumberArray(data: RegistrationProgress | number[]): data is number[] {
    const numberArray = data as number[];
    return numberArray.length !== undefined;
  }

  validateDba() {
    const dbaControl = this.vendorForm.controls['dba'];
    const dbaValue = dbaControl.value;

    if (isInvalidDba(dbaValue)) {
      dbaControl.patchValue('');

      this.ToastService.addErrorToast(
        'Invalid DBA',
        DBA_VALIDATION_ERROR_MESSAGE,
        { centerHeader: true }
      );
    }
  }

  ngOnInit(): void {
    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (!this.vendorForm) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor form.', {
        returnError: true
      });
    }

    if (!this.localizationInfo) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Localization Information.',
        { returnError: true }
      );
    }

    if (!this.streetAddressForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Street Address form.',
        { returnError: true }
      );
    }

    if (!this.mailingAddressForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Mailing Address form.',
        { returnError: true }
      );
    }

    if (!this.remitToAddressForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Remit To Address form.',
        { returnError: true }
      );
    }

    if (!this.selectedSmallBusinessOptionsFormArray) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing selected small business options form array.',
        { returnError: true }
      );
    }

    if (!this.ClientCompanyVendorMap) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor map.',
        { returnError: true }
      );
    }

    if (!this.CompanyIdToCompanyOfficesMap) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client offices map.',
        { returnError: true }
      );
    }

    const country = this.vendorForm.controls['country'].value;

    this.VendorService.getVendorV2(this.VendorId, {
      // SSN can only be retrieved by applicable permissions on backend.
      attributes: JSON.stringify(['id', 'ssn']),
      aliasedAttributes: JSON.stringify([
        'hasFullPackageClient',
        'hasSignedAnyVendorAgreement',
        'hasMultipleClients',
        'hasSSN'
      ])
    }).subscribe({
      next: (Vendor) => {
        if (!Vendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Vendor information.',
            { returnError: true }
          );
        }
        this.Vendor = Vendor;
        this._cdr.markForCheck();
      }
    });

    this.SubNationalDivisionService.getSubNationalDivisions({
      country: country === 'US' ? 'USA' : 'Canada'
    }).subscribe({
      next: (data: SubNationalDivision[]) => {
        this.states = data;
        this._cdr.markForCheck();
      }
    });

    this.vendorForm.controls['allowAfterHoursService'].valueChanges.subscribe({
      next: () => this.afterHoursPhoneRequirement()
    });

    this.vendorForm.controls['isPubliclyTraded'].valueChanges.subscribe({
      next: () => this.isPubliclyTradedCHanged()
    });

    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (role && this.editPermissions.has(role)) {
      const {
        controls: { companyType, llcType }
      } = this.vendorForm;
      companyType.enable();
      llcType.enable();
    }
  }
}
